import { Map, OrderedMap, OrderedSet, Record, Set } from 'immutable';

import { FullBetSlipState } from '../types';
import type { ReduxAction } from 'app/redux/types';

import { LanguageStore, NOTIFY_ABOUT_CHANGED_LANG, NotifyAboutChangedLang, SupportedLang } from 'app/lang';
import type {
  BackendBet,
  BackendBets,
  BackendEntities,
  BackendEntitiesDiff,
  BackendStakes,
} from 'app/lib/betting/betting-entities';
import { updateField } from 'app/lib/betting/betting-entities/reducers/utils';
import { APPLY_MATCHES, ApplyMatches } from 'app/lib/signalr';
import { APPLY_INTERESTING_MATCHES_STATE } from 'app/modules/LandingPage/InterestingMatches/actions';
import { APPLY_STATE, LiveHubApplyState } from 'app/modules/Live/actions/liveHub';
import {
  APPLY_PREMATCH_MATCHES,
  APPLY_STATE as APPLY_PREMATCH_STATE,
  PrematchApplyState,
  PrematchMatchesApplyState,
} from 'app/modules/Prematch/actions';

import { CLEANUP, Cleanup } from '../actions';
import {
  ACCEPT_PRICE_CHANGES, AcceptPriceChanges,
  CANCEL_DELETES, CANCEL_INSERT, CANCEL_REPLACE, CancelDeletes, CancelInsert, CancelReplace,
  COMPLETE_REPLACE, CompleteReplace,
  DELETE_FROM_BET_SLIP, DeleteFromBetSlip,
  INSERT_FAILED_ENTRY_IN_BET_SLIP, INSERT_IN_BET_SLIP, INSERT_IN_QUEUE_FOR_ADD,
  INSERT_IN_QUEUE_FOR_DELETE,
  InsertFailedEntryInBetSlip,
  InsertInBetSlip,
  InsertInQueueForAdd,
  InsertInQueueForDelete,
  REPLACE_ENTRIES, ReplaceEntries,
  SWITCH_TO_TOTAL_AMOUNT, SwitchToTotalAmount,
} from '../actions/entries';
import {
  SWITCH_TO_ALLOW_PRICE_PREFERENCE,
  SwitchToAllowPricePreference,
} from '../actions/preferences';
import { SUBMIT_STAKE_FAILURE, SubmitStakeFailure } from '../actions/submission';
import { AllowingPricePreference, BetErrorCode, MatchErrorCode } from '../constants';
import { EntriesBets, EntriesStakes, EntriesState, RecordOfEntriesState } from '../types/entries';
import { createImmutableMatchObjects, updateStakesNames } from '../utils/entries';

// --

export const initialState = Record<EntriesState>({
  inserts: OrderedSet(),
  deletes: OrderedSet(),
  bets: OrderedMap(),
  betIds: Set(),
  stakes: OrderedMap(),
  matches: OrderedMap(),
  sports: OrderedMap(),
  totalAmountActive: false,
  lastAddedBet: null,
}, 'entries');

// TODO: Сделать (удалить, когда будет тип для APPLY_INTERESTING_MATCHES_STATE)
type ApplyInterestingMatches = ReduxAction<typeof APPLY_INTERESTING_MATCHES_STATE, BackendEntities>;

export type EntriesActions =
  | InsertInQueueForAdd
  | InsertInBetSlip
  | InsertFailedEntryInBetSlip
  | CancelInsert
  | InsertInQueueForDelete
  | DeleteFromBetSlip
  | CancelDeletes
  | ReplaceEntries
  | CompleteReplace
  | CancelReplace
  | LiveHubApplyState
  | PrematchApplyState
  | PrematchMatchesApplyState
  | ApplyInterestingMatches
  | ApplyMatches
  | SwitchToTotalAmount
  | AcceptPriceChanges
  | SubmitStakeFailure
  | SwitchToAllowPricePreference
  | NotifyAboutChangedLang
  | Cleanup;

// --- Добавление ставки

function insertInQueueForAdd(
  state: RecordOfEntriesState,
  payload: InsertInQueueForAdd['payload'],
): RecordOfEntriesState {
  const entryId = payload.bet.entity.id;
  const inserts = state.get('inserts');

  if (inserts.has(entryId)) {
    return state;
  }

  if (state.get('bets').has(entryId)) {
    return state;
  }

  return state.set('inserts', inserts.add(entryId));
}

type InsertPayload =
  | InsertInBetSlip['payload']
  | InsertInQueueForAdd['payload']
  | InsertFailedEntryInBetSlip['payload'];

function insertInBetSlip(
  state: RecordOfEntriesState,
  payload: InsertPayload,
): RecordOfEntriesState {
  const entryId = payload.bet.entity.id;
  const inserts = state.get('inserts');

  if (!inserts.has(entryId)) {
    return state;
  }

  const bets = state.get('bets');
  const betIds = state.get('betIds');

  if (bets.has(entryId)) {
    return state;
  }
  state = state.set('betIds', betIds.add(entryId));

  state = state.set('bets', bets.set(entryId, payload.bet));

  // --

  const stakes = state.get('stakes');
  const stakeId = payload.stake.entity.id;
  if (!stakes.has(stakeId)) {
    state = state.set('stakes', stakes.set(stakeId, payload.stake));
  }
  else {
    const betIds = state.get('stakes')?.get(stakeId)?.get('betIds');
    if (!betIds) {
      console.error('BetSlip > reducer > entries > insertInBetSlip: `betIds` not found');
      return state;
    }
    state = state.setIn(['stakes', stakeId, 'betIds'], betIds.push(entryId));
  }

  // --

  const matches = state.get('matches');
  const matchId = payload.match.entity.id;
  if (!matches.has(matchId)) {
    state = state.set('matches', matches.set(matchId, payload.match));
  }
  else {
    const betIds = state.get('matches')?.get(matchId)?.get('betIds');
    if (!betIds) {
      console.error('BetSlip > reducer > entries > insertInBetSlip: `betIds` not found');
      return state;
    }
    state = state.setIn(['matches', matchId, 'betIds'], betIds.push(entryId));
  }

  // --

  const sports = state.get('sports');
  const sportId = payload.sport.entity.id;
  if (!sports.has(sportId)) {
    state = state.set('sports', sports.set(sportId, payload.sport));
  }
  else {
    const sport = state.get('sports')?.get(sportId);

    if (!sport) {
      console.error('BetSlip > reducer > entries > insertInBetSlip: `sport` not found');
      return state;
    }

    if (sport.matchIds.includes(matchId)) {
      state = state.setIn(['sports', sportId], sport);
    }
    else {
      const updatedSports = sport.merge({
        matchIds: sport.matchIds.push(matchId),
      });
      state = state.setIn(['sports', sportId], updatedSports);
    }
  }

  // --

  state = state.set('inserts', inserts.remove(entryId));
  state = state.set('lastAddedBet', { betId: entryId, matchId });

  return state;
}

function cancelInsert(
  state: RecordOfEntriesState,
  { betId: entryId }: CancelInsert['payload'],
): RecordOfEntriesState {
  const inserts = state.get('inserts');

  if (!inserts.has(entryId)) {
    return state;
  }

  return state.set('inserts', inserts.remove(entryId));
}

// --- Удаление ставки

function insertInQueueForDelete(
  state: RecordOfEntriesState,
  payload: InsertInQueueForDelete['payload'],
): RecordOfEntriesState {
  return state.set(
    'deletes',
    payload.entries.reduce((deletes, { betId }) => {
      return deletes.add(betId);
    }, state.get('deletes')),
  );
}

function deleteFromBetSlip(
  state: RecordOfEntriesState,
  payload: DeleteFromBetSlip['payload'],
): RecordOfEntriesState {
  let bets = state.get('bets');
  let betIds = state.get('betIds');
  let stakes = state.get('stakes');
  let matches = state.get('matches');
  let sports = state.get('sports');
  let deletes = state.get('deletes');
  let totalAmountActive = state.get('totalAmountActive');

  payload.entries.forEach(({
    betId,
    stakeId,
    matchId,
    sportId,
  }) => {
    betIds = betIds.delete(betId);
    bets = bets.delete(betId);

    if (bets.size <= 1) {
      totalAmountActive = false;
    }

    // ---

    const stake = stakes.get(stakeId);

    if (!stake) {
      console.error('deleteFromBetSlip -> `stake` not found');
      return;
    }

    if (stake.betIds.size <= 1) { // the last bet in the related stake
      stakes = stakes.delete(stakeId); // remove the whole stake entry
    }
    else {
      // remove current bet id from the related stake entry
      const betIds = stakes.get(stakeId)?.get('betIds');
      if (!betIds) {
        console.error('BetSlip > reducer > entries > deleteFromBetSlip: `betIds` not found');
        return state;
      }
      stakes = stakes.setIn([stakeId, 'betIds'], betIds.delete(betIds.indexOf(betId)));
    }

    // ---

    const match = matches.get(matchId);

    if (!match) {
      console.error('deleteFromBetSlip -> `match` not found');
      return;
    }

    if (match.betIds.size <= 1) { // the last bet in the related match
      matches = matches.delete(matchId); // remove the whole match entry
    }
    else {
      // remove current bet id from the related match entry
      const betIds = matches.get(matchId)?.get('betIds');
      if (!betIds) {
        console.error('BetSlip > reducer > entries > deleteFromBetSlip: `betIds` not found');
        return state;
      }
      matches = matches.setIn([matchId, 'betIds'], betIds.delete(betIds.indexOf(betId)));
    }

    // ---

    const sport = sports.get(sportId);

    if (!sport) {
      console.error('deleteFromBetSlip -> `sport` not found');
      return;
    }

    sport.matchIds.forEach((matchId) => {
      const match = matches.get(matchId);

      if (!match) {
        if (sport.matchIds.size <= 1) { // the last match in the related sport
          sports = sports.delete(sportId); // remove the whole sport entry
        }
        else {
          // remove current match id from the related sport entry
          const sport = sports.get(sportId);
          const matchIds = sport?.matchIds;
          if (!matchIds) {
            console.error('BetSlip > reducer > entries > deleteFromBetSlip: `matchIds` not found');
            return;
          }
          const updatedSports = sport?.merge({
            matchIds: matchIds.delete(matchIds.indexOf(matchId)),
          });
          sports = sports.set(sportId, updatedSports);
        }
      }
    });

    deletes = deletes.remove(betId);
  });

  return state.merge({ bets, betIds, stakes, matches, sports, deletes, totalAmountActive });
}

function cancelDeletes(
  state: RecordOfEntriesState,
  { betIds }: CancelDeletes['payload'],
): RecordOfEntriesState {
  return state.set(
    'deletes',
    betIds.reduce((deletes, betId) => {
      return deletes.remove(betId);
    }, state.get('deletes')),
  );
}

// ---
type StateItem = { entity: { type?: number } };

function updateMatchItem<Entries extends OrderedMap<number, StateItem>>(
  itemName: string,
  items: BackendBets | BackendStakes | undefined,
  itemsState: Entries,
  removedItems: number[] | undefined,
): Entries {
  if (removedItems) {
    removedItems.forEach((itemId) => {
      if (itemsState.has(itemId)) {
        itemsState = updateField(itemsState, [itemId, 'entity'], 'isOpen', false);
      }
    });
  }

  if (items) {
    const itemsKeys = Object.keys(items);

    itemsKeys.forEach((itemKey) => {
      const item = items[itemKey];
      const { id, isOpen, names } = item;

      const itemState = itemsState.get(id);

      if (itemState) {
        if (itemName === 'bets' && (item as BackendBet).price !== undefined) {
          itemsState = updateField(
            itemsState,
            [id, 'entity'],
            'price',
            (item as BackendBet).price,
          );
        }
        if (isOpen !== undefined) {
          itemsState = updateField(itemsState, [id, 'entity'], 'isOpen', isOpen);
        }
        if (names !== undefined) {
          itemsState = updateField(itemsState, [id, 'entity'], 'names', Map(names));
        }
      }
    });
  }

  return itemsState;
}

function updateMatchEntities(
  state: RecordOfEntriesState,
  { matches, objects, removedMatches }: BackendEntitiesDiff,
  langCode: SupportedLang,
): RecordOfEntriesState {
  if (!(matches || removedMatches || objects)) {
    return state;
  }

  let matchesState = state.get('matches');
  let stakesState = state.get('stakes');
  let betsState = state.get('bets');

  if (removedMatches) {
    removedMatches.forEach((matchId) => {
      if (matchesState.has(matchId)) {
        matchesState = updateField(matchesState, [matchId, 'entity'], 'isOpen', false);
      }
    });
  }

  let updatedMatches = Set<number>();

  if (matches) {
    Object.values(matches).forEach((match) => {
      if (!match.id || !matchesState.has(match.id)) {
        return;
      }

      const {
        id, isOpen, name, stakes, bets, removedBets, removedStakes, objects: matchObjects,
      } = match;

      const path = [id, 'entity'];

      if (isOpen !== undefined) {
        matchesState = updateField(matchesState, path, 'isOpen', isOpen);
      }
      if (name !== undefined) {
        matchesState = updateField(matchesState, path, 'name', name);
      }
      if (stakes !== undefined || removedStakes !== undefined) {
        stakesState = updateMatchItem<EntriesStakes>(
          'stakes',
          stakes,
          stakesState,
          removedStakes,
        );
      }
      if (bets !== undefined || removedBets !== undefined) {
        betsState = updateMatchItem<EntriesBets>(
          'bets',
          bets,
          betsState,
          removedBets,
        );
      }
      if (matchObjects !== undefined) {
        updatedMatches = updatedMatches.add(match.id);
        const matchEntity = matchesState.get(match.id)?.get('entity');

        if (!matchEntity?.objects) {
          return;
        }

        const immutableObjects = createImmutableMatchObjects(
          matchEntity.objects,
          objects,
          matchObjects,
        );

        const values = updateStakesNames(langCode, immutableObjects, stakesState, betsState);

        stakesState = values.stakesState;
        betsState = values.betsState;
        matchesState = updateField(matchesState, path, 'objects', immutableObjects);
      }
    });
  }

  if (objects) {
    matchesState.forEach((match, matchId) => {
      if (updatedMatches.has(matchId) || !match.entity.objects) {
        return;
      }

      const hasChangedObject = match.entity.objects.some((object) => object.id in objects);

      if (!hasChangedObject) {
        return;
      }

      const immutableObjects = createImmutableMatchObjects(
        match.entity.objects,
        objects,
      );

      const values = updateStakesNames(langCode, immutableObjects, stakesState, betsState);

      const path = [matchId, 'entity'];

      stakesState = values.stakesState;
      betsState = values.betsState;
      matchesState = updateField(matchesState, path, 'objects', immutableObjects);
    });
  }

  state = state.set('matches', matchesState);
  state = state.set('stakes', stakesState);
  state = state.set('bets', betsState);

  return state;
}

function updateNamesAfterChangeLang(
  state: RecordOfEntriesState,
  lang: SupportedLang,
): RecordOfEntriesState {
  let betsState = state.get('bets');
  let stakesState = state.get('stakes');

  state.get('matches').forEach((match) => {
    const values = updateStakesNames(lang, match.entity.objects, stakesState, betsState);
    stakesState = values.stakesState;
    betsState = values.betsState;
  });

  state = state.set('stakes', stakesState);
  state = state.set('bets', betsState);

  return state;
}

function acceptPriceChanges(
  state: RecordOfEntriesState,
  allowPricePreference: AllowingPricePreference,
): RecordOfEntriesState {
  return state.update('bets', (bets) => (
    bets.map((entry) => {
      if (!entry.entity.price || !entry.price) {
        return entry;
      }

      if (entry.entity.price === entry.price) {
        return entry; // price doesn't changed
      }

      const isIncrease = allowPricePreference !== AllowingPricePreference.NOTHING
        && entry.entity.price > entry.price;
      const isDecrease = allowPricePreference === AllowingPricePreference.EVERYTHING
        && entry.entity.price < entry.price;

      if (isIncrease) {
        return entry.set('price', entry.entity.price);
      }

      if (isDecrease) {
        return entry.set('price', entry.entity.price);
      }

      return entry;
    })
  ));
}

// ---

function updateStateAfterFailure(
  state: RecordOfEntriesState,
  payload: SubmitStakeFailure['payload'],
): RecordOfEntriesState {
  payload?.betErrors?.forEach((bet) => {
    if (bet.code === BetErrorCode.PRICE_CHANGED && bet.newPrice) {
      state = updateField(state, ['bets', bet.betId, 'entity'], 'price', bet.newPrice);
    }
    else if (bet.code === BetErrorCode.RECEPTION_CLOSED) {
      state = updateField(state, ['bets', bet.betId, 'entity'], 'isOpen', false);
    }
  });

  payload?.matchErrors?.forEach((match) => {
    if (match.code === MatchErrorCode.RECEPTION_CLOSED) {
      state = updateField(state, ['matches', match.matchId, 'entity'], 'isOpen', false);
    }
  });

  return state;
}

// ---

export default function (
  state = initialState(),
  action: EntriesActions,
  globalState: FullBetSlipState,
): RecordOfEntriesState {
  switch (action.type) {
    case INSERT_IN_QUEUE_FOR_ADD:
      return insertInQueueForAdd(state, action.payload);

    case INSERT_IN_BET_SLIP:
    case INSERT_FAILED_ENTRY_IN_BET_SLIP:
      return insertInBetSlip(state, action.payload);

    case CANCEL_INSERT:
      return cancelInsert(state, action.payload);

    case INSERT_IN_QUEUE_FOR_DELETE:
      return insertInQueueForDelete(state, action.payload);

    case DELETE_FROM_BET_SLIP:
      return deleteFromBetSlip(state, action.payload);

    case CANCEL_DELETES:
      return cancelDeletes(state, action.payload);

    case REPLACE_ENTRIES:
      state = insertInQueueForDelete(state, action.payload);
      return insertInQueueForAdd(state, action.payload.by);

    case COMPLETE_REPLACE:
      state = deleteFromBetSlip(state, action.payload);
      return insertInBetSlip(state, action.payload.by);

    case CANCEL_REPLACE:
      state = cancelDeletes(state, action.payload);
      return cancelInsert(state, { betId: action.payload.byBetId });

    case NOTIFY_ABOUT_CHANGED_LANG:
      return updateNamesAfterChangeLang(state, action.payload.language);

    case APPLY_STATE:
    case APPLY_INTERESTING_MATCHES_STATE:
    case APPLY_PREMATCH_MATCHES:
    case APPLY_PREMATCH_STATE:
    case APPLY_MATCHES: {
      if (!state.get('bets').size && !state.get('stakes').size && !state.get('matches').size) {
        return state;
      }

      state = updateMatchEntities(state, action.payload.diff, LanguageStore.getLang());

      const { allowPricePreference } = globalState?.preferences || {};

      if (allowPricePreference) {
        state = acceptPriceChanges(state, allowPricePreference);
      }

      return state;
    }

    case SUBMIT_STAKE_FAILURE: {
      if (action.payload.isBigStake) {
        return state;
      }

      if (!state.get('bets').size && !state.get('stakes').size && !state.get('matches').size) {
        return state;
      }

      state = updateStateAfterFailure(state, action.payload);

      const { allowPricePreference } = globalState?.preferences || {};

      if (allowPricePreference) {
        state = acceptPriceChanges(state, allowPricePreference);
      }

      return state;
    }

    case SWITCH_TO_TOTAL_AMOUNT:
      return state.update('totalAmountActive', () => action.payload.value);

    case ACCEPT_PRICE_CHANGES:
      return acceptPriceChanges(state, AllowingPricePreference.EVERYTHING);

    case SWITCH_TO_ALLOW_PRICE_PREFERENCE: {
      const allowPricePreference = globalState?.preferences.allowPricePreference
        ?? AllowingPricePreference.NOTHING;

      return acceptPriceChanges(state, allowPricePreference);
    }

    case CLEANUP:
      return initialState();

    default:
      return state;
  }
}
